import { IParseProduct } from '../models/quotations.models';

/**
 * WB-8278: resolution buckets used to group order lines by how much user attention
 * they still need. Order of the union is the display order: matched → review → unmatched.
 */
export type LineMatchGroup = 'matched' | 'review' | 'unmatched';

export const LINE_MATCH_GROUP_ORDER: LineMatchGroup[] = ['matched', 'review', 'unmatched'];

/** How the products list orders its rows: document order, or bucketed by resolution status. */
export type LineGroupingMode = 'default' | 'resolution';

/** One rendered group: its bucket, its rows and its row count (used in the header). */
export interface LineGroupView {
    key: LineMatchGroup;
    lines: Array<IParseProduct & { _isUnmatched?: boolean }>;
    count: number;
}

/**
 * Mirrors `isPreferredProductMatch` in both table components, numeric-value test
 * included: without it a `preferred` line whose value is not a count falls through
 * to the alternatives branch there, and it has to fall through here too.
 */
function isPreferredMatch(product: IParseProduct): boolean {
    const method = (product.ai_match_method || '').toLowerCase();
    const value = (product.ai_match_value || '').trim();
    return (method === 'preferred' || method === 'preferred_global') && /^\d+$/.test(value);
}

/**
 * Classifies one order line into its resolution bucket.
 *
 * The precedence mirrors `getProductStatusIcon` step for step, so a group header can
 * never contradict the icon and tooltip on the row underneath it:
 *
 *   `manual-match-icon`    → matched
 *   `preferred-match-icon` → matched
 *   `check-list-icon`      → review
 *   `check-icon`           → matched
 *
 *  1. `_isUnmatched` — set by `combinedLines` on rows sourced from
 *     `ai_details.unmatched_order_lines`. These have no product at all, so no other
 *     signal on them is meaningful, and they have no status icon to agree with.
 *  2. Manual intent (`matched` / `added_manually`) — a human already resolved this row,
 *     which overrides whatever the AI originally reported. An AI row later matched by
 *     hand keeps its `ai_match_method`, so testing the method first would wrongly park a
 *     resolved line back under "review".
 *  3. A preferred (frequency) match. Ranks ABOVE `has_alternatives` because the icon
 *     helper does: a preferred line that also carries alternatives shows
 *     `preferred-match-icon`, so grouping it under "review" would contradict its own row.
 *  4. Alternatives — the "needs review" case.
 *  5. Everything else is a good match. Deliberately the fallback rather than a positive
 *     test on the known-good methods: legacy and non-AI lines carry no `ai_match_method`
 *     at all and must not be surfaced as needing attention.
 *
 * `hybrid_reranked` is deliberately NOT tested here even though the ticket describes
 * review as "lines coming from semantic re-ranking". `getProductStatusIcon` does not
 * look at it either, so testing it would put a row showing a plain `check-icon` and
 * `tooltip_ok` under the Review header.
 *
 * Known gap, reviewed and accepted rather than assumed away: nothing couples the two
 * signals. `has_alternatives` is `bool(order_line.alternative_ids)` server-side, built
 * from the `alternatives` array of the AI payload (blank sku/name entries skipped),
 * which is a different part of the response from `ai_match_method`. So a re-ranked line
 * that carried no usable alternatives files under Matched. That matches what the row
 * itself already showed before this feature existed, but Matched starts collapsed, so
 * such a line now sits behind a collapsed header. If that combination turns out to be
 * real, the fix is to add the semantic case to `getProductStatusIcon` FIRST — icon,
 * tooltip and the FTUE anchor derived from it all move together — and only then here.
 */
export function lineMatchGroup(product: IParseProduct & { _isUnmatched?: boolean }): LineMatchGroup {
    if (product._isUnmatched) return 'unmatched';
    if (product.matched || product.added_manually) return 'matched';
    if (isPreferredMatch(product)) return 'matched';
    if (product.has_alternatives) return 'review';

    return 'matched';
}

/**
 * The grouped-mode state shared by the two product lists (the side-by-side panel and the order
 * detail page). Both render the same rows from a `combinedLines` getter, so the mode, the collapse
 * set and the bucketing live here rather than being written twice.
 *
 * Neither the mode nor the collapse set is persisted: both reset with the component.
 */
export class LineGroupingController {
    mode: LineGroupingMode = 'default';

    private collapsed = new Set<LineMatchGroup>();
    private cache: { key: unknown[]; groups: LineGroupView[] } | null = null;

    /**
     * Switches mode, returning true when it actually changed. Entering `resolution` resets the
     * collapse state — `matched` starts collapsed so attention lands on review and unmatched.
     */
    setMode(mode: LineGroupingMode): boolean {
        if (this.mode === mode) return false;
        this.mode = mode;
        if (mode === 'resolution') this.collapsed = new Set<LineMatchGroup>(['matched']);
        return true;
    }

    get isGrouped(): boolean {
        return this.mode === 'resolution';
    }

    isCollapsed(key: LineMatchGroup): boolean {
        return this.collapsed.has(key);
    }

    toggle(key: LineMatchGroup): void {
        const next = new Set(this.collapsed);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        this.collapsed = next;
    }

    /**
     * Buckets `lines` in fixed matched → review → unmatched order, dropping empty groups so no
     * header renders for them. Input order is preserved inside each group, so an active column
     * sort still applies within a bucket.
     *
     * `cacheKey` is the caller's source arrays and lengths. This is read from the template on every
     * change-detection pass, so the SAME array has to come back while those are unchanged —
     * otherwise `trackBy` re-creates every row.
     *
     * The key is identity plus length, NOT content: mutating `matched`, `has_alternatives` or
     * `ai_match_method` on a line already in one of these arrays leaves it in its old bucket. That
     * holds today because a resolved line comes back through a refetch that replaces the array
     * (`refetchQuotationPreservingPagination`) — the same assumption `ftueAnchoredLines()` makes.
     * A caller that starts editing those fields in place has to add something to the key that
     * changes with them, the way the order detail page passes its sort column.
     */
    groups(
        lines: () => Array<IParseProduct & { _isUnmatched?: boolean }>,
        cacheKey: unknown[],
    ): LineGroupView[] {
        const hit = this.cache;
        if (hit && hit.key.length === cacheKey.length && hit.key.every((v, i) => v === cacheKey[i])) {
            return hit.groups;
        }

        const buckets = new Map<LineMatchGroup, Array<IParseProduct & { _isUnmatched?: boolean }>>(
            LINE_MATCH_GROUP_ORDER.map((key) => [key, []])
        );
        lines().forEach((line) => buckets.get(lineMatchGroup(line))!.push(line));

        const groups = LINE_MATCH_GROUP_ORDER
            .map((key) => ({ key, lines: buckets.get(key)!, count: buckets.get(key)!.length }))
            .filter((group) => group.count > 0);

        this.cache = { key: cacheKey, groups };
        return groups;
    }

    readonly trackByGroup = (_: number, group: LineGroupView): string => group.key;
}
